import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiGet, apiPost, getApiError } from '../../../lib/api';
import { useSubmitGuard } from '../../../lib/useSubmitGuard';
import { useAuth } from '../../../lib/auth';
import { Card } from '../../../components/Card';
import { PageScaffold } from '../../../components/PageScaffold';
import { Button } from '../../../components/Button';
import { IconButton } from '../../../components/IconButton';
import { Input } from '../../../components/Input';
import { Money } from '../../../components/Money';
import { EmptyState } from '../../../components/EmptyState';
import { Stepper } from '../../../components/Stepper';
import { cn } from '../../../lib/cn';
import { FAST_TRACK_DELIVERY_FEE } from '@kalachand/shared';
import { cart, useCart } from '../../../lib/cart';
import { lineDiscountAmount, type DiscountMode } from '../../../lib/discount';
import { DiscountInput } from '../../../components/DiscountInput';
import { CustomerCreateForm, type CreatedCustomer } from '../CustomerCreateForm';
import { CustomSizeModal } from '../CustomSizeModal';

interface Shop {
  id: string;
  code: string;
  name: string;
}
interface GiftSizeOption {
  productId: string;
  sku: string;
  size: string | null;
  available: number | null;
}
interface GiftDef {
  productId: string | null;
  giftModelId: string | null;
  sku: string | null;
  name: string;
  qty: number;
  available: number | null;
  sizeOptions: GiftSizeOption[] | null;
}
interface Product {
  id: string;
  sku: string;
  name: string;
  modelId?: string | null;
  salePrice: string | null;
  basePrice: string;
  vatRate: string;
  category?: {
    name: string;
    fulfillmentStrategy?: string;
    deliveryRequired?: boolean;
    allowsCustomSize?: boolean;
  } | null;
  stockLevels?: { onHand: number; reserved: number; display: number }[];
  gifts?: GiftDef[];
}

interface LineDraft {
  productId: string;
  product: { sku: string; name: string; vatRate: number };
  qty: number;
  unitPrice: number;
  discountMode: DiscountMode;
  discountValue: number;
  /** True when a stock-tracked product has zero available across all locations.
   *  A quote can still be created — this only drives the UI warning. */
  outOfStock?: boolean;
  // ── Bespoke mattress size (mirrors the order flow) ──
  // When isCustom, `productId` is a synthetic id (so custom lines never merge),
  // `matchedVariantId` is the real variant sent to the API, and `unitPrice` is the
  // server-derived ladder price (locked in the UI; the server recomputes it).
  isCustom?: boolean;
  matchedVariantId?: string;
  customWidthCm?: number;
  customLengthCm?: number;
  matchedSize?: string;
  // Free gift(s) the model bundles + the sales girl's opt-in choice. Gifts are
  // opt-in (not auto-added); the choice is stored on the quote and carried into
  // the order at convert time.
  gifts?: GiftDef[];
  includeGift?: boolean;
  // Chosen size per MODEL gift, keyed by giftModelId → chosen variant productId.
  giftSelections?: Record<string, string>;
}

// Pre-pick a size per model gift (first in-stock, else first). Mirrors the order.
function defaultGiftSelections(gifts?: GiftDef[]): Record<string, string> {
  const sel: Record<string, string> = {};
  for (const g of gifts ?? []) {
    if (!g.giftModelId || !g.sizeOptions?.length) continue;
    const inStock = g.sizeOptions.find((o) => o.available == null || o.available >= g.qty);
    sel[g.giftModelId] = (inStock ?? g.sizeOptions[0]!).productId;
  }
  return sel;
}

// The API-shaped size picks for a line's model gifts: [{ giftModelId, productId }].
function giftSelectionsFor(l: LineDraft): { giftModelId: string; productId: string }[] {
  return (l.gifts ?? [])
    .filter((g) => g.giftModelId)
    .map((g) => ({ giftModelId: g.giftModelId!, productId: l.giftSelections?.[g.giftModelId!] ?? '' }))
    .filter((s) => s.productId);
}

/**
 * Out of stock only applies to stock-tracked products (STOCK_AT_LOCATION).
 * Made-to-order / supplier categories don't draw from StockLevel, so they're
 * never flagged. Available = max(0, onHand − reserved − display) summed over
 * locations (a showroom piece is present but not sellable).
 */
function productOutOfStock(p: Product): boolean {
  if (p.category?.fulfillmentStrategy !== 'STOCK_AT_LOCATION') return false;
  const available = (p.stockLevels ?? []).reduce(
    (sum, lv) => sum + Math.max(0, Number(lv.onHand) - Number(lv.reserved) - Number(lv.display)),
    0,
  );
  return available <= 0;
}

function isoDay(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const VALIDITY_DAYS = 10; // Kalachand policy: a quotation is an estimate valid 10 days

export function NewQuotationPage(): JSX.Element {
  const { user, perms } = useAuth();
  const navigate = useNavigate();
  // Cross-shop seller (owner/admin/operations) — keyed off the permission, not
  // the absence of a home shop (ops now home at HQ but still pick the shop).
  const isHqUser = perms.has('orders.view_all_shops');

  const [step, setStep] = useState(1);

  // ── Shop (HQ users pick; shop-scoped staff default to whichever shop
  // they're currently switched to in the nav — "Currently at") ──────────────
  const shopsQuery = useQuery({
    queryKey: ['shops'],
    queryFn: () => apiGet<Shop[]>('/shops'),
    enabled: isHqUser,
  });
  const shops = shopsQuery.data ?? [];
  const [shopId, setShopId] = useState<string>(user?.activeShopId ?? user?.homeShopId ?? '');
  useEffect(() => {
    if (!shopId && shops[0]) setShopId(shops[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shops.length]);
  const sellingShop = shops.find((s) => s.id === shopId);

  // ── Customer ──────────────────────────────────────────────────────────────
  const [customer, setCustomer] = useState<CreatedCustomer | null>(null);

  // ── Items ───────────────────────────────────────────────────────────────--
  // Seed from the catalogue cart (when arriving via "Add to quotation"), then
  // let the user search for more. Finishes/colours are ignored on a quote.
  // Bespoke custom-size items carry over their synthetic id + matched variant; the
  // server re-derives + recomputes the price on save (see quotations.controller).
  const cartView = useCart();
  const [lines, setLines] = useState<LineDraft[]>(() =>
    cartView.items.map((c) => ({
      productId: c.productId,
      product: { sku: c.sku, name: c.name, vatRate: c.vatRate },
      qty: c.qty,
      unitPrice: c.unitPrice,
      discountMode: 'pct',
      discountValue: 0,
      isCustom: c.isCustom,
      matchedVariantId: c.matchedVariantId,
      customWidthCm: c.customWidthCm,
      customLengthCm: c.customLengthCm,
      matchedSize: c.matchedSize,
      gifts: c.gifts ?? [],
      includeGift: false,
      giftSelections: defaultGiftSelections(c.gifts),
    })),
  );
  // Product picked for a bespoke custom size (opens the modal).
  const [customFor, setCustomFor] = useState<Product | null>(null);
  const addCustomLine = (line: LineDraft): void => setLines((existing) => [...existing, line]);
  const addLine = (p: Product): void => {
    setLines((existing) => {
      const found = existing.find((l) => l.productId === p.id);
      if (found) return existing.map((l) => (l.productId === p.id ? { ...l, qty: l.qty + 1 } : l));
      return [
        ...existing,
        {
          productId: p.id,
          product: { sku: p.sku, name: p.name, vatRate: Number(p.vatRate) },
          qty: 1,
          unitPrice: Number(p.salePrice ?? p.basePrice),
          discountMode: 'pct',
          discountValue: 0,
          outOfStock: productOutOfStock(p),
          gifts: p.gifts ?? [],
          includeGift: false,
          giftSelections: defaultGiftSelections(p.gifts),
        },
      ];
    });
  };
  const updateLine = (id: string, patch: Partial<LineDraft>): void =>
    setLines((existing) => existing.map((l) => (l.productId === id ? { ...l, ...patch } : l)));
  const removeLine = (id: string): void =>
    setLines((existing) => existing.filter((l) => l.productId !== id));

  // ── Validity + notes ───────────────────────────────────────────────────---
  const [validUntil, setValidUntil] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() + VALIDITY_DAYS);
    return isoDay(d);
  });
  const [notes, setNotes] = useState('');
  // Fast Track (priority) delivery — opt-in flat fee quoted on the estimate.
  const [fastTrack, setFastTrack] = useState(false);

  // ── Totals ──────────────────────────────────────────────────────────────--
  const vatExempt = customer?.vatExcluded ?? false;
  const totals = useMemo(() => {
    let subtotal = 0;
    let vat = 0;
    let discountTotal = 0;
    for (const l of lines) {
      const lineDisc = lineDiscountAmount(l.qty, l.unitPrice, l.discountMode, l.discountValue);
      // Catalogue prices are VAT-INCLUSIVE — the line gross already contains VAT,
      // so EXTRACT it (× rate/(1+rate)) rather than adding on top. Mirrors the
      // backend's vatInclusiveSplit (2dp) so the preview matches the saved quote.
      const grossIncl = l.qty * l.unitPrice - lineDisc;
      const r = l.product.vatRate;
      const lineVat = vatExempt ? 0 : Math.round(((grossIncl * r) / (1 + r)) * 100) / 100;
      const lineNet = grossIncl - lineVat;
      subtotal += lineNet;
      vat += lineVat;
      discountTotal += lineDisc;
    }
    const fastTrackFee = fastTrack ? FAST_TRACK_DELIVERY_FEE : 0;
    return { subtotal, vat, discountTotal, fastTrackFee, total: subtotal + vat + fastTrackFee };
  }, [lines, vatExempt, fastTrack]);

  // ── Submit ──────────────────────────────────────────────────────────────--
  // Blocks a same-tick double-tap on "Save quotation" from creating two quotes.
  const guard = useSubmitGuard();
  const create = useMutation({
    mutationFn: async () => {
      if (!shopId) throw new Error('Pick a shop');
      if (!customer) throw new Error('Pick a customer');
      if (lines.length === 0) throw new Error('Add at least one line item');
      return apiPost<{ id: string; number: string }>('/quotations', {
        shopId,
        customerId: customer.id,
        validUntil: validUntil ? new Date(validUntil) : undefined,
        notes: notes || undefined,
        fastTrack,
        lines: lines.map((l) => ({
          // Custom lines send the matched standard variant as the productId; the
          // synthetic line id lives only in the UI.
          productId: l.isCustom ? l.matchedVariantId! : l.productId,
          qty: l.qty,
          // Custom lines: server recomputes the price from the size ladder; we
          // still send it (ignored) so the payload stays uniform.
          unitPrice: l.unitPrice,
          // Custom sizes are never discounted (enforced server-side too).
          discount: l.isCustom ? 0 : lineDiscountAmount(l.qty, l.unitPrice, l.discountMode, l.discountValue),
          ...(l.isCustom ? { customWidthCm: l.customWidthCm, customLengthCm: l.customLengthCm } : {}),
          // Only send the gift opt-in for lines that actually bundle one.
          includeGift: (l.gifts?.length ?? 0) > 0 ? !!l.includeGift : undefined,
          // Chosen size(s) for this line's model gift(s) — only when opted in.
          ...(l.includeGift ? { giftSelections: giftSelectionsFor(l) } : {}),
        })),
      });
    },
    onSuccess: (data) => {
      cart.clear();
      navigate(`/quotations/${data.id}`);
    },
    onSettled: guard.release,
  });

  // ── Step wizard validity ──────────────────────────────────────────────────
  const itemsValid = lines.length > 0 && !!shopId;
  const customerValid = !!customer;
  const stepValid = (s: number): boolean => (s === 1 ? itemsValid : s === 2 ? customerValid : true);
  const canGoTo = (target: number): boolean => {
    for (let s = 1; s < target; s++) if (!stepValid(s)) return false;
    return true;
  };

  const daysRemaining = (() => {
    if (!validUntil) return null;
    const target = new Date(validUntil);
    return Math.ceil((target.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  })();
  const custDisplay = customer
    ? customer.type === 'company'
      ? (customer.companyName ?? customer.name)
      : customer.name
    : '';

  return (
    <PageScaffold
      back={{ to: '/quotations', label: 'Quotations' }}
      title={['Add items', 'Choose customer', 'Review & send'][step - 1]}
      contentClassName="space-y-4"
      subtitle={
        <>
          New quotation · step {step} of 3 ·{' '}
          {sellingShop ? `Quoting from ${sellingShop.name}` : 'Price proposal'} · {lines.length}{' '}
          line{lines.length === 1 ? '' : 's'} · <Money value={totals.total} className="text-ink" />
        </>
      }
      actions={
        <Button
          variant="ghost"
          onClick={() => {
            cart.clear();
            navigate(-1);
          }}
        >
          Discard
        </Button>
      }
      footer={
        <div className="flex items-center justify-between w-full gap-3">
          <div>
            {step > 1 && (
              <Button variant="secondary" onClick={() => setStep(step - 1)}>
                ← Back
              </Button>
            )}
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm text-ink-3 hidden sm:block">
              Total <Money value={totals.total} className="text-ink font-medium ml-1" />
            </div>
            {step < 3 ? (
              <Button
                disabled={!stepValid(step)}
                onClick={() => stepValid(step) && setStep(step + 1)}
              >
                {step === 1 ? 'Customer →' : 'Review →'}
              </Button>
            ) : (
              <Button
                loading={create.isPending}
                disabled={!customer || lines.length === 0 || !shopId}
                onClick={() => guard.run(() => create.mutate())}
              >
                Save quotation
              </Button>
            )}
          </div>
        </div>
      }
    >
      <Stepper
        current={step}
        onStep={(t) => {
          if (canGoTo(t)) setStep(t);
        }}
        steps={[
          { label: 'Items', done: itemsValid },
          { label: 'Customer', done: customerValid },
          { label: 'Review', done: false },
        ]}
      />

      {/* ── STEP 1 — Items ───────────────────────────────────────────────── */}
      {step === 1 && (
        <div className="space-y-6">
          {isHqUser && shops.length > 0 && (
            <Card
              title="Quoting from"
              subtitle="Owner / admin / operations can quote on behalf of any shop"
            >
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Shop</span>
                <select
                  value={shopId}
                  onChange={(e) => setShopId(e.target.value)}
                  className="kc-input"
                >
                  {shops.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.code} · {s.name}
                    </option>
                  ))}
                </select>
              </label>
            </Card>
          )}
          <ProductPicker onAdd={addLine} onCustom={setCustomFor} />
          <LinesTable
            lines={lines}
            onUpdate={updateLine}
            onRemove={removeLine}
            vatExempt={vatExempt}
          />
        </div>
      )}

      {customFor?.modelId && (
        <CustomSizeModal
          modelId={customFor.modelId}
          fallbackName={customFor.name}
          onClose={() => setCustomFor(null)}
          onConfirm={(r) => {
            addCustomLine({
              productId: `custom-${crypto.randomUUID()}`,
              product: {
                sku: `Priced as ${r.matchedSize}`,
                name: `${r.modelName} — Custom ${r.widthCm}×${r.lengthCm} cm`,
                vatRate: r.vatRate,
              },
              qty: 1,
              unitPrice: r.unitPrice,
              discountMode: 'pct',
              discountValue: 0,
              isCustom: true,
              matchedVariantId: r.matchedVariantId,
              customWidthCm: r.widthCm,
              customLengthCm: r.lengthCm,
              matchedSize: r.matchedSize,
            });
            setCustomFor(null);
          }}
        />
      )}

      {/* ── STEP 2 — Customer ────────────────────────────────────────────── */}
      {step === 2 && <CustomerPicker value={customer} onChange={setCustomer} />}

      {/* ── STEP 3 — Review & send ───────────────────────────────────────── */}
      {step === 3 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card
              title="Review"
              subtitle="Check everything before saving the quotation"
              bodyClassName="divide-y divide-rule-2"
            >
              {/* Customer */}
              <div className="flex items-start justify-between gap-3 pb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-ink-3">Customer</div>
                  <div className="text-ink font-medium">{custDisplay}</div>
                  <div className="text-xs text-ink-3 kc-mono">
                    {customer?.customerCode} · {customer?.phone}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setStep(2)}>
                  Edit
                </Button>
              </div>
              {/* Items */}
              <div className="pt-3">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-wider text-ink-3">
                    Items ({lines.length})
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
                    Edit
                  </Button>
                </div>
                <ul className="mt-2 divide-y divide-rule-2">
                  {lines.map((l) => (
                    <li
                      key={l.productId}
                      className="flex items-baseline justify-between gap-3 py-1.5 text-sm"
                    >
                      <span className="text-ink">
                        {l.qty} × {l.product.name}
                      </span>
                      <Money
                        value={
                          l.qty * l.unitPrice -
                          lineDiscountAmount(l.qty, l.unitPrice, l.discountMode, l.discountValue)
                        }
                        className="text-ink-3"
                      />
                    </li>
                  ))}
                </ul>
              </div>
            </Card>

            {create.isError && (
              <div className="bg-negative-soft text-negative px-3 py-2 rounded text-sm border border-negative/20">
                {getApiError(create.error).message}
              </div>
            )}
          </div>

          <div className="space-y-6">
            <Card title="Totals">
              <dl className="space-y-2 text-sm">
                <Row k="Subtotal" v={<Money value={totals.subtotal} className="text-ink" />} />
                {totals.discountTotal > 0 && (
                  <Row
                    k="Discount"
                    v={<Money value={-totals.discountTotal} className="text-warning" />}
                  />
                )}
                <Row
                  k="VAT"
                  v={
                    vatExempt ? (
                      <span className="text-ink-3">Exempt</span>
                    ) : (
                      <Money value={totals.vat} className="text-ink" />
                    )
                  }
                />
                {totals.fastTrackFee > 0 && (
                  <Row
                    k="Fast Track Delivery"
                    v={<Money value={totals.fastTrackFee} className="text-ink" />}
                  />
                )}
                <Row
                  k={<span className="font-medium text-ink">TOTAL</span>}
                  v={<Money value={totals.total} className="kc-serif text-2xl text-accent" />}
                />
              </dl>
              {/* Fast Track (priority) delivery — flat VAT-inclusive fee (T&C §9) */}
              <label className="mt-3 pt-3 border-t border-rule flex items-start justify-between gap-3 cursor-pointer">
                <div>
                  <div className="text-sm text-ink">⚡ Fast Track delivery</div>
                  <div className="text-xs text-ink-3 mt-0.5">
                    Quote priority delivery — flat{' '}
                    <Money value={FAST_TRACK_DELIVERY_FEE} className="font-medium" /> incl. VAT.
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={fastTrack}
                  onChange={(e) => setFastTrack(e.target.checked)}
                  className="mt-1 h-4 w-4 shrink-0 accent-accent"
                />
              </label>
            </Card>

            <Card title="Validity">
              <Input
                type="date"
                label="Valid until"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
                hint={
                  daysRemaining != null && daysRemaining >= 0
                    ? `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} from today`
                    : 'Pick a date'
                }
              />
              <div className="flex gap-2 mt-2">
                {[10, 14, 30, 60].map((days) => (
                  <button
                    key={days}
                    type="button"
                    onClick={() => {
                      const d = new Date();
                      d.setDate(d.getDate() + days);
                      setValidUntil(isoDay(d));
                    }}
                    className={cn(
                      'text-[11px] uppercase tracking-wide border border-rule rounded px-2 py-1 hover:bg-paper-2 transition',
                      daysRemaining === days ? 'border-accent text-accent' : 'text-ink-3',
                    )}
                  >
                    {days}d
                  </button>
                ))}
              </div>
            </Card>

            <Card title="Notes" subtitle="Optional — appears on the quotation PDF">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="e.g. Includes free delivery within Port Louis area…"
                className="kc-input w-full resize-none"
              />
            </Card>
          </div>
        </div>
      )}
    </PageScaffold>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Product picker
// ─────────────────────────────────────────────────────────────────────────────
function ProductPicker({
  onAdd,
  onCustom,
}: {
  onAdd: (p: Product) => void;
  onCustom: (p: Product) => void;
}): JSX.Element {
  const [q, setQ] = useState('');
  const query = q.trim();
  const search = useQuery({
    queryKey: ['products-quote-search', query],
    queryFn: () => apiGet<Product[]>('/products', { q: query }),
    enabled: query.length > 0,
  });
  const list = (search.data ?? []).slice(0, 12);
  return (
    <Card title="Add products" subtitle="Search by SKU or name and tap to add">
      <Input
        id="prod-q"
        placeholder="Search products…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoComplete="off"
      />

      {query.length === 0 ? (
        <p className="mt-3 text-xs text-ink-3">
          Start typing a product name or SKU, or browse the{' '}
          <Link to="/catalogue" className="text-accent hover:underline">
            catalogue
          </Link>
          .
        </p>
      ) : (
        <div className="mt-3 border border-rule rounded-md divide-y divide-rule-2 max-h-72 overflow-y-auto">
          {search.isLoading ? (
            <div className="text-sm text-ink-3 px-4 py-6 text-center">Searching…</div>
          ) : list.length === 0 ? (
            <div className="text-sm text-ink-3 px-4 py-6 text-center">
              No products match “{query}”.
            </div>
          ) : (
            list.map((p) => {
              const onSale = !!p.salePrice;
              const oos = productOutOfStock(p);
              // Locally made-to-order mattresses can be quoted at a bespoke size.
              const canCustomSize = !!p.modelId && !!p.category?.allowsCustomSize;
              return (
                <div key={p.id} className="flex items-stretch transition hover:bg-paper-2">
                  <button
                    type="button"
                    onClick={() => onAdd(p)}
                    className="flex-1 min-w-0 text-left px-4 py-2.5 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="text-sm text-ink truncate flex items-center gap-2">
                        <span className="truncate">{p.name}</span>
                        {oos && (
                          <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-warning bg-warning-soft border border-warning/20 rounded px-1.5 py-0.5">
                            Out of stock
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-ink-3 kc-mono truncate">
                        {p.sku}
                        {p.category?.name ? ` · ${p.category.name}` : ''}
                      </div>
                    </div>
                    <div className="shrink-0 text-right leading-tight">
                      <Money
                        value={p.salePrice ?? p.basePrice}
                        className={cn('text-sm', onSale && 'text-accent font-medium')}
                      />
                      {onSale && (
                        <Money
                          value={p.basePrice}
                          className="block text-[10px] text-ink-4 line-through"
                        />
                      )}
                    </div>
                  </button>
                  {canCustomSize && (
                    <button
                      type="button"
                      onClick={() => onCustom(p)}
                      className="shrink-0 px-3 my-1.5 mr-2 ml-1 border-l border-rule-2 text-[11px] font-medium text-accent hover:underline"
                      title="Quote a bespoke width × length"
                    >
                      Custom size
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Lines table
// ─────────────────────────────────────────────────────────────────────────────
function LinesTable({
  lines,
  onUpdate,
  onRemove,
  vatExempt,
}: {
  lines: LineDraft[];
  onUpdate: (id: string, patch: Partial<LineDraft>) => void;
  onRemove: (id: string) => void;
  vatExempt: boolean;
}): JSX.Element {
  if (lines.length === 0) {
    return (
      <Card title="Line items" bodyClassName="p-0">
        <EmptyState
          title="No items yet"
          description="Use the search above, or browse the catalogue."
        />
      </Card>
    );
  }
  const outOfStockCount = lines.filter((l) => l.outOfStock).length;
  return (
    <Card
      title={`Line items (${lines.length})`}
      subtitle={`${lines.reduce((s, l) => s + l.qty, 0)} units`}
      bodyClassName="p-0"
    >
      {outOfStockCount > 0 && (
        <div className="mx-4 mt-4 flex items-start gap-2 rounded-md border border-warning/20 bg-warning-soft px-3 py-2 text-sm text-warning">
          <span aria-hidden>⚠</span>
          <span>
            {outOfStockCount === 1 ? 'An item is' : `${outOfStockCount} items are`} out of
            stock. You can still create this quotation — stock is only checked when it's
            converted to an order.
          </span>
        </div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
            <th className="px-6 py-2 font-medium">Product</th>
            <th className="px-3 py-2 font-medium text-right">Qty</th>
            <th className="px-3 py-2 font-medium text-right">Unit</th>
            <th className="px-3 py-2 font-medium text-right">Discount</th>
            <th className="px-3 py-2 font-medium text-right">VAT</th>
            <th className="px-6 py-2 font-medium text-right">Total</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => {
            const lineDisc = lineDiscountAmount(
              l.qty,
              l.unitPrice,
              l.discountMode,
              l.discountValue,
            );
            // VAT-inclusive catalogue price → extract VAT, don't add on top.
            const grossIncl = l.qty * l.unitPrice - lineDisc;
            const r = l.product.vatRate;
            const lineVat = vatExempt ? 0 : Math.round(((grossIncl * r) / (1 + r)) * 100) / 100;
            const lineNet = grossIncl - lineVat;
            return (
              <tr key={l.productId} className="border-b border-rule-2">
                <td className="px-6 py-3">
                  <div className="text-ink flex items-center gap-2">
                    <span>{l.product.name}</span>
                    {l.isCustom && (
                      <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-accent bg-accent-soft px-1.5 py-0.5 rounded">
                        Custom
                      </span>
                    )}
                    {l.outOfStock && (
                      <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-warning bg-warning-soft border border-warning/20 rounded px-1.5 py-0.5">
                        Out of stock
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-ink-3 kc-mono">{l.product.sku}</div>
                  {/* Free-gift opt-in — the choice is stored on the quote and
                      carried into the order at convert time (gifts are opt-in). */}
                  {(l.gifts?.length ?? 0) > 0 && (
                    <>
                      <label className="flex items-start gap-1.5 mt-2 text-[11px] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!l.includeGift}
                          onChange={(e) => onUpdate(l.productId, { includeGift: e.target.checked })}
                          className="accent-accent mt-0.5"
                        />
                        <span className={l.includeGift ? 'text-accent' : 'text-ink-3'}>
                          🎁 Add free gift
                          {l.gifts!.length === 1 ? ` (${l.gifts![0]?.name})` : ` (${l.gifts!.length} items)`}
                        </span>
                      </label>
                      {/* Size picker per MODEL gift — the sales girl chooses which
                          size; stored on the quote and carried into the order. */}
                      {l.includeGift &&
                        l.gifts!
                          .filter((g) => g.giftModelId && (g.sizeOptions?.length ?? 0) > 0)
                          .map((g) => (
                            <div
                              key={g.giftModelId}
                              className="flex items-center gap-1.5 mt-1 ml-5 text-[11px]"
                            >
                              <span className="text-ink-3">{g.name} size</span>
                              <select
                                className="kc-input kc-input-sm py-0.5"
                                value={l.giftSelections?.[g.giftModelId!] ?? ''}
                                onChange={(e) =>
                                  onUpdate(l.productId, {
                                    giftSelections: {
                                      ...l.giftSelections,
                                      [g.giftModelId!]: e.target.value,
                                    },
                                  })
                                }
                              >
                                {g.sizeOptions!.map((o) => (
                                  <option key={o.productId} value={o.productId}>
                                    {o.size ?? o.sku}
                                  </option>
                                ))}
                              </select>
                            </div>
                          ))}
                    </>
                  )}
                </td>
                <td className="px-3 py-3 text-right">
                  <input
                    type="number"
                    min={1}
                    value={l.qty}
                    onChange={(e) =>
                      onUpdate(l.productId, { qty: Math.max(1, Number(e.target.value)) })
                    }
                    className="kc-input w-16 text-right kc-mono"
                  />
                </td>
                <td className="px-3 py-3 text-right">
                  {l.isCustom ? (
                    // Bespoke price is server-derived from the size ladder — locked here.
                    <span
                      className="inline-flex items-center justify-end gap-1 kc-mono text-ink"
                      title="Priced from the standard size ladder"
                    >
                      <Money value={l.unitPrice} />
                      <span className="text-ink-4">🔒</span>
                    </span>
                  ) : (
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={l.unitPrice}
                      onChange={(e) =>
                        onUpdate(l.productId, { unitPrice: Math.max(0, Number(e.target.value)) })
                      }
                      className="kc-input w-24 text-right kc-mono"
                    />
                  )}
                </td>
                <td className="px-3 py-3 text-right">
                  {l.isCustom ? (
                    // Custom sizes are never discounted (enforced server-side too).
                    <span className="text-ink-4">—</span>
                  ) : (
                    <DiscountInput
                      qty={l.qty}
                      unitPrice={l.unitPrice}
                      mode={l.discountMode}
                      value={l.discountValue}
                      onChange={(next) =>
                        onUpdate(l.productId, { discountMode: next.mode, discountValue: next.value })
                      }
                    />
                  )}
                </td>
                <td className="px-3 py-3 text-right kc-mono text-ink-3">
                  {vatExempt ? <span className="italic">—</span> : <Money value={lineVat} muted />}
                </td>
                <td className="px-6 py-3 text-right">
                  <Money value={lineNet + lineVat} />
                </td>
                <td className="pr-4">
                  <IconButton
                    aria-label={`Remove ${l.product.name}`}
                    variant="danger"
                    size="sm"
                    onClick={() => onRemove(l.productId)}
                  >
                    ×
                  </IconButton>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Customer picker — search / create / edit (mirrors the order flow)
// ─────────────────────────────────────────────────────────────────────────────
function CustomerPicker({
  value,
  onChange,
}: {
  value: CreatedCustomer | null;
  onChange: (c: CreatedCustomer | null) => void;
}): JSX.Element {
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);

  const search = useQuery({
    queryKey: ['customer-search-quote', q],
    queryFn: () => apiGet<CreatedCustomer[]>('/customers/lookup', { q }),
    enabled: q.length >= 3,
  });

  if (value) {
    return (
      <CustomerCreateForm
        key={value.id}
        customer={value}
        cancelLabel="Change customer"
        onCreated={onChange}
        onCancel={() => onChange(null)}
      />
    );
  }

  if (creating) {
    return (
      <CustomerCreateForm
        onCreated={onChange}
        onCancel={() => setCreating(false)}
        initialPhone={/^\+?[\d\s-]+$/.test(q) ? q : ''}
      />
    );
  }

  return (
    <Card
      title="Customer"
      subtitle="Find an existing customer by NIC or phone — or create a new record"
    >
      <div className="space-y-3">
        <Input
          id="cust-q"
          placeholder="Enter NIC or phone (min 3 characters)…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="border border-rule rounded divide-y divide-rule">
          {q.length < 3 ? (
            <div className="text-sm text-ink-3 px-4 py-3">
              Type at least 3 characters of a NIC or phone number.
            </div>
          ) : search.isLoading ? (
            <div className="text-sm text-ink-3 px-4 py-3">Searching…</div>
          ) : (search.data ?? []).length === 0 ? (
            <div className="text-sm text-ink-3 px-4 py-3">
              No matches.{' '}
              <Button type="button" variant="tertiary" size="xs" onClick={() => setCreating(true)}>
                Create a new customer
              </Button>
            </div>
          ) : (
            (search.data ?? []).map((c) => (
              <button
                key={c.id}
                className="w-full text-left px-4 py-2.5 hover:bg-paper-2 transition flex items-baseline justify-between gap-3"
                onClick={() => onChange(c)}
              >
                <div>
                  <div className="text-sm text-ink flex items-center gap-2">
                    {c.type === 'company' ? (c.companyName ?? c.name) : c.name}
                    {c.type === 'company' && (
                      <span className="text-[10px] uppercase tracking-wider text-accent">
                        company
                      </span>
                    )}
                    {c.vatExcluded && (
                      <span className="text-[10px] uppercase tracking-wider text-info">
                        VAT excl.
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-ink-3 kc-mono">
                    {c.phone}
                    {c.email ? ` · ${c.email}` : ''}
                  </div>
                </div>
                <span className="text-xs text-ink-4 kc-mono">{c.customerCode}</span>
              </button>
            ))
          )}
        </div>
        <Button type="button" variant="tertiary" size="xs" onClick={() => setCreating(true)}>
          + Create a new customer
        </Button>
      </div>
    </Card>
  );
}

function Row({ k, v }: { k: React.ReactNode; v: React.ReactNode }): JSX.Element {
  return (
    <div className="flex justify-between items-center">
      <dt className="text-ink-3">{k}</dt>
      <dd>{v}</dd>
    </div>
  );
}
